import React, { useMemo } from "react";
import type { PrintTemplate } from "@repo/types";
import type { PrintDataFields } from "../hooks/use-print-data";
import { InfoField } from "../components/InfoField";
import { SectionHeader } from "../components/SectionHeader";
import { ARBRE_FIELDS, ARBRE_SECTIONS } from "./registry";
import { resolveLayout } from "./resolve-layout";
import type { ResolvedItem } from "./types";

export interface HtmlPrintTemplateProps {
  template: PrintTemplate;
  data: PrintDataFields;
  inventaireId: number;
  /** Marges de la page : par défaut compactes (aperçu) ; la vraie page d'impression passe les marges larges de la fiche. */
  pageClassName?: string;
  /** Rendu d'un bloc riche (légende de risque, tableau sanitaire…). Par défaut : un cadre pointillé. */
  renderBlock?: (item: ResolvedItem) => React.ReactNode;
}

/** Sans cette option, les navigateurs retirent les fonds colorés à l'impression. */
const KEEP_COLORS_WHEN_PRINTED: React.CSSProperties = {
  printColorAdjust: "exact",
  WebkitPrintColorAdjust: "exact",
};

const colorStyle = (
  background?: string,
  color?: string,
): React.CSSProperties => ({
  ...(background ? { backgroundColor: background } : {}),
  ...(color ? { color } : {}),
});

const placeholder = (item: ResolvedItem) => (
  <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
    {item.label}
  </div>
);

/** Peintre HTML : ne fait que du style à partir du résultat de `resolveLayout` (aucune décision de mise en page ici). */
export function HtmlPrintTemplate({
  template,
  data,
  inventaireId,
  renderBlock = placeholder,
  pageClassName = "px-4",
}: HtmlPrintTemplateProps) {
  const sections = useMemo(
    () => resolveLayout(template, ARBRE_SECTIONS, ARBRE_FIELDS, data),
    [template, data],
  );
  const { header } = template;

  return (
    <div className={`page mx-auto bg-card ${pageClassName}`}>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:gap-6">
        {header.showLogo &&
          (data.organizationLogo ? (
            <img
              src={data.organizationLogo}
              alt={data.organizationName}
              className="h-auto max-h-[200px] w-[200px] shrink-0"
            />
          ) : (
            <div className="flex h-[110px] w-[200px] shrink-0 items-center justify-center border border-border bg-muted">
              <span className="text-xs text-muted-foreground">Logo</span>
            </div>
          ))}
        <div className="text-xs sm:text-sm">
          {header.showOrganisation && (
            <>
              <p className="text-sm font-bold text-[#4E5155] sm:text-base">
                {data.organizationName}
              </p>
              {data.organizationAddress && <p>{data.organizationAddress}</p>}
              {data.organizationCity && <p>{data.organizationCity}</p>}
            </>
          )}
          {header.showSiret && data.organizationSiret && (
            <p>
              <span className="text-xs font-bold text-[#4E5155] sm:text-sm">
                SIRET :
              </span>{" "}
              {data.organizationSiret}
            </p>
          )}
          {header.showRecenseur && (
            <p className="mt-2">
              <span className="text-xs font-bold text-[#4E5155] sm:text-sm">
                Recenseur :
              </span>{" "}
              {data.user?.prenom?.toUpperCase()} {data.user?.nom?.toUpperCase()}
            </p>
          )}
        </div>
      </div>

      <SectionHeader
        variant="primary"
        style={{
          ...colorStyle(header.titleBg, header.titleColor),
          ...KEEP_COLORS_WHEN_PRINTED,
        }}
      >
        <span className="block sm:inline">FICHE DE SYNTHESE ARBRE </span>
        <span className="block sm:inline sm:before:mx-2 sm:before:content-['-']">
          {inventaireId}
        </span>
      </SectionHeader>

      {sections.map((section) => (
        <section key={section.id} className="break-inside-avoid">
          {section.title && (
            <SectionHeader
              style={{
                ...colorStyle(
                  section.style?.titleBg,
                  section.style?.titleColor,
                ),
                ...KEEP_COLORS_WHEN_PRINTED,
              }}
            >
              {section.title}
            </SectionHeader>
          )}
          <div
            className="grid gap-4 border-b border-border py-4 text-xs"
            style={{
              ...(section.style?.background
                ? {
                    backgroundColor: section.style.background,
                    ...KEEP_COLORS_WHEN_PRINTED,
                  }
                : {}),
              gridTemplateColumns: `repeat(${section.columns.length}, minmax(0, 1fr))`,
            }}
          >
            {section.columns.map((column, index) => (
              <div
                key={index}
                className={
                  section.id === "identification" ||
                  column.every((item) => item.kind === "block")
                    ? undefined
                    : "border-l border-l-[0.5px] border-[#000] pl-2"
                }
              >
                {column.map((item) =>
                  item.kind === "block" ? (
                    <div key={item.key}>{renderBlock(item)}</div>
                  ) : (
                    <InfoField
                      key={item.key}
                      label={item.label}
                      value={item.value}
                    />
                  ),
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
